/**
 * Import a broker CSV into a book — mock 2 and 2b in
 * `docs/mockups/position-sizing.html`.
 *
 * ## Three steps, and nothing lands before the third
 *
 * 1. **File** — pick it and where it goes. It is uploaded and parsed at once so
 *    the screen can show what it read; the server keeps it *staged*. When the
 *    header names are not recognised, the column card (2b) asks only for what
 *    it could not tell, and the answer is saved for the next file with the same
 *    columns.
 * 2. **Duplicates** — every fill that may already be in the chosen book, each
 *    with its own skip/keep. The user's call, never the app's (plan §3 rule 1).
 * 3. **Result** — what was written, and what needs the user.
 *
 * Cancel before step 3 deletes the staged upload, so an abandoned import leaves
 * no trace. The server is the source of every number shown; this screen keeps
 * no parse of its own.
 */

import { useMemo, useState } from "react";
import {
  REQUIRED_FIELDS,
  cancelImport,
  commitImport,
  updateImport,
  uploadCsv,
  type Book,
  type ColumnMapping,
  type Destination,
  type MappingField,
  type StagedImport,
} from "../api/sizing";

const FIELD_LABEL: Record<MappingField, string> = {
  date: "Date",
  time: "Time",
  symbol: "Symbol",
  action: "Action",
  quantity: "Quantity",
  price: "Price",
  fees: "Fees",
  commission: "Commission",
  fillId: "Fill id",
  description: "Description",
  right: "Put / call",
  strike: "Strike",
  expiry: "Expiry",
};

/** Shown in the column card; the rest are for exports that split an option's parts into columns. */
const CARD_FIELDS: MappingField[] = [
  "date", "time", "symbol", "action", "quantity", "price", "fees", "commission", "fillId",
];

const TIMEZONES = [
  "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
  "UTC", "Europe/London", "Asia/Kolkata",
];

/** Duplicate times are shown in Pacific, the app's reporting zone. */
const PT = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/Los_Angeles",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

type Step = "file" | "duplicates" | "result";

export function ImportWizard({
  books,
  initialBookId,
  onDone,
  onCancel,
}: {
  books: Book[];
  /** Opened from a book's "Add file": that book is the destination. */
  initialBookId?: string;
  /** Committed: the book it landed in, so the list can refresh. */
  onDone: () => void;
  onCancel: () => void;
}) {
  const [step, setStep] = useState<Step>("file");
  const [file, setFile] = useState<File | null>(null);
  const [staged, setStaged] = useState<StagedImport | null>(null);
  const [target, setTarget] = useState<"new" | "existing">(initialBookId ? "existing" : "new");
  const [newName, setNewName] = useState("");
  const [bookId, setBookId] = useState(initialBookId ?? books[0]?.id ?? "");
  const [broker, setBroker] = useState("");
  const [mapping, setMapping] = useState<ColumnMapping | null>(null);
  const [showColumns, setShowColumns] = useState(false);
  const [decisions, setDecisions] = useState<Record<string, "keep" | "skip">>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showProblems, setShowProblems] = useState(false);

  const destination = (): Destination =>
    target === "existing" && bookId ? { bookId } : { newBook: newName.trim() || baseName(file?.name) };

  /** Every server call goes through here: busy, error, and the answer adopted whole. */
  async function run(fn: () => Promise<StagedImport>): Promise<StagedImport | null> {
    setBusy(true);
    setError(null);
    try {
      const next = await fn();
      setStaged(next);
      if (next.mapping) setMapping(next.mapping);
      if (next.duplicates) {
        setDecisions(Object.fromEntries(next.duplicates.map((d) => [String(d.row), d.decision])));
      }
      return next;
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      return null;
    } finally {
      setBusy(false);
    }
  }

  async function pick(f: File) {
    setFile(f);
    if (!newName) setNewName(baseName(f.name));
    if (staged) await cancelImport(staged.id).catch(() => undefined);
    const dest: Destination =
      target === "existing" && bookId ? { bookId } : { newBook: newName.trim() || baseName(f.name) };
    const next = await run(() => uploadCsv(f, dest, broker));
    if (next?.missing?.length) setShowColumns(true);
  }

  /** Destination or broker changed after the upload: the server re-matches duplicates. */
  async function retarget() {
    if (!staged) return;
    await run(() => updateImport(staged.id, { ...destination(), broker }));
  }

  async function applyMapping(next: ColumnMapping) {
    setMapping(next);
    if (staged) await run(() => updateImport(staged.id, { mapping: next }));
  }

  async function cancel() {
    if (staged && staged.status === "staged") await cancelImport(staged.id).catch(() => undefined);
    onCancel();
  }

  async function commit() {
    if (!staged) return;
    const done = await run(() => commitImport(staged.id, decisions));
    if (done) setStep("result");
  }

  const missing = staged?.missing ?? [];
  const ready = !!staged && missing.length === 0 && !!staged.counts;
  const dups = staged?.duplicates ?? [];
  const skipping = dups.filter((d) => decisions[String(d.row)] !== "keep").length;
  const importing = (staged?.counts?.fills ?? 0) - skipping;
  const destLabel =
    target === "existing" ? books.find((b) => b.id === bookId)?.name ?? "the book" : newName || "the new book";

  return (
    <div style={PANEL}>
      <div style={STEPS}>
        {(["file", "duplicates", "result"] as Step[]).map((s, i) => (
          <span key={s} style={stepStyle(s, step)}>
            {i + 1} {s === "file" ? "File" : s === "duplicates" ? "Duplicates" : "Result"}
          </span>
        ))}
      </div>

      {error && <div style={ERROR}>{error}</div>}

      {step === "file" && (
        <>
          <DropZone busy={busy} onFile={(f) => void pick(f)} />
          {file && (
            <div style={CARD}>
              <span style={LABEL}>File</span>
              <div>
                {file.name} · {formatBytes(file.size)}
                {staged?.rows !== undefined && <> · {staged.rows.toLocaleString()} rows</>}
              </div>
              {staged && (
                <div style={MUTED}>
                  {staged.mappingSaved
                    ? "Columns recognised from a file you mapped before."
                    : missing.length
                      ? null
                      : "Columns recognised from their names."}{" "}
                  {ready && (
                    <button type="button" style={LINK} onClick={() => setShowColumns((v) => !v)}>
                      {showColumns ? "hide columns" : "check columns"}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          <div style={CARD}>
            <span style={LABEL}>Into which book?</span>
            <label style={ROW}>
              <input type="radio" checked={target === "new"} onChange={() => setTarget("new")} /> New book
              <input
                style={{ ...INPUT, width: 180 }}
                value={newName}
                placeholder="Book name"
                onChange={(e) => setNewName(e.target.value)}
                onFocus={() => setTarget("new")}
                onBlur={() => void retarget()}
              />
            </label>
            <label style={ROW}>
              <input
                type="radio"
                checked={target === "existing"}
                disabled={books.length === 0}
                onChange={() => setTarget("existing")}
              />{" "}
              Existing
              <select
                style={INPUT}
                value={bookId}
                disabled={books.length === 0}
                onChange={(e) => {
                  setBookId(e.target.value);
                  setTarget("existing");
                }}
              >
                {books.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </label>
            <label style={ROW}>
              Broker
              <input
                style={{ ...INPUT, width: 140 }}
                value={broker}
                placeholder="e.g. Schwab (optional)"
                onChange={(e) => setBroker(e.target.value)}
                onBlur={() => void retarget()}
              />
            </label>
            {staged && (
              <button type="button" style={{ ...BTN, alignSelf: "flex-start" }} disabled={busy} onClick={() => void retarget()}>
                Apply destination
              </button>
            )}
          </div>

          {staged && missing.length > 0 && (
            <div style={WARN}>
              {missing.length} required column{missing.length === 1 ? " was" : "s were"} not recognised. Pick{" "}
              {missing.length === 1 ? "it" : "them"} below — saved for next time.
            </div>
          )}

          {staged && mapping && (showColumns || missing.length > 0) && (
            <ColumnsCard
              headers={staged.headers ?? []}
              mapping={mapping}
              missing={missing}
              busy={busy}
              onChange={(next) => void applyMapping(next)}
            />
          )}

          {staged?.preview && staged.preview.length > 0 && (showColumns || missing.length > 0) && (
            <div style={CARD}>
              <span style={LABEL}>First rows, as read</span>
              <div style={SCROLL}>
                <table style={TABLE}>
                  <thead>
                    <tr>
                      {(staged.headers ?? []).map((h, i) => (
                        <th key={i} style={TH}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {staged.preview.map((r, i) => (
                      <tr key={i}>
                        {(staged.headers ?? []).map((_, j) => (
                          <td key={j} style={TD}>
                            {r[j] ?? ""}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {ready && staged?.counts && (
            <div style={MUTED}>
              Read {staged.counts.fills.toLocaleString()} fills ({staged.counts.stock} stock,{" "}
              {staged.counts.option} option) → {staged.counts.roundTrips} round trips
              {staged.range && (
                <>
                  {" "}
                  · {staged.range[0].slice(0, 10)} → {staged.range[1].slice(0, 10)}
                </>
              )}
              {!!staged.problems?.length && <> · {staged.problems.length} rows could not be read</>}
            </div>
          )}

          <div style={FOOT}>
            <button type="button" style={BTN} onClick={() => void cancel()}>
              Cancel
            </button>
            <span style={{ flex: 1 }} />
            <button
              type="button"
              style={PRIMARY}
              disabled={!ready || busy}
              onClick={() => setStep("duplicates")}
            >
              Next: check duplicates
            </button>
          </div>
        </>
      )}

      {step === "duplicates" && staged && (
        <>
          {dups.length === 0 ? (
            <div style={MUTED}>No fill in this file is already in {destLabel}.</div>
          ) : (
            <>
              <div style={WARN}>
                {dups.length} fill{dups.length === 1 ? "" : "s"} may already be in <b>{destLabel}</b>. You
                decide each one.
              </div>
              <div style={ROW}>
                <button
                  type="button"
                  style={BTN}
                  onClick={() =>
                    setDecisions((d) => ({
                      ...d,
                      ...Object.fromEntries(dups.filter((x) => x.match === "fillId").map((x) => [String(x.row), "skip"])),
                    }))
                  }
                >
                  Skip all by fill id ({dups.filter((x) => x.match === "fillId").length})
                </button>
                <button
                  type="button"
                  style={BTN}
                  onClick={() => setDecisions(Object.fromEntries(dups.map((x) => [String(x.row), "skip"])))}
                >
                  Skip all
                </button>
                <button
                  type="button"
                  style={BTN}
                  onClick={() => setDecisions(Object.fromEntries(dups.map((x) => [String(x.row), "keep"])))}
                >
                  Keep all
                </button>
              </div>
              <div style={{ ...SCROLL, maxHeight: 260 }}>
                <table style={TABLE}>
                  <thead>
                    <tr>
                      <th style={TH}>Match</th>
                      <th style={TH}>Symbol</th>
                      <th style={TH}>Side</th>
                      <th style={{ ...TH, textAlign: "right" }}>Qty</th>
                      <th style={{ ...TH, textAlign: "right" }}>Price</th>
                      <th style={TH}>Time PT</th>
                      <th style={TH}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dups.map((d) => (
                      <tr key={d.row}>
                        <td style={{ ...TD, color: d.match === "hash" ? "var(--qf-warning, #ffb74d)" : undefined }}>
                          {d.match === "fillId" ? "fill id" : "hash"}
                        </td>
                        <td style={TD}>{d.symbol}</td>
                        <td style={TD}>{d.effect && !d.side ? d.effect : d.side}</td>
                        <td style={NUM}>{d.quantity}</td>
                        <td style={NUM}>{d.price.toFixed(2)}</td>
                        <td style={TD}>{PT.format(new Date(d.executedAt))}</td>
                        <td style={TD}>
                          <select
                            style={INPUT}
                            value={decisions[String(d.row)] ?? "skip"}
                            onChange={(e) =>
                              setDecisions((prev) => ({ ...prev, [String(d.row)]: e.target.value as "keep" | "skip" }))
                            }
                          >
                            <option value="skip">Skip</option>
                            <option value="keep">Keep</option>
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={MUTED}>
                “hash” = no broker id; matched on time, symbol, side, quantity and price. Two genuine identical
                fills are possible — that is why it asks.
              </div>
            </>
          )}
          <div style={FOOT}>
            <button type="button" style={BTN} onClick={() => setStep("file")}>
              Back
            </button>
            <button type="button" style={BTN} onClick={() => void cancel()}>
              Cancel
            </button>
            <span style={{ flex: 1 }} />
            <button type="button" style={PRIMARY} disabled={busy || importing <= 0} onClick={() => void commit()}>
              Import {importing.toLocaleString()} fill{importing === 1 ? "" : "s"}
            </button>
          </div>
        </>
      )}

      {step === "result" && staged?.summary && (
        <Result
          summary={staged.summary}
          bookName={staged.book?.name ?? destLabel}
          showProblems={showProblems}
          onToggleProblems={() => setShowProblems((v) => !v)}
          onDone={onDone}
        />
      )}
    </div>
  );
}

function ColumnsCard({
  headers,
  mapping,
  missing,
  busy,
  onChange,
}: {
  headers: string[];
  mapping: ColumnMapping;
  missing: MappingField[];
  busy: boolean;
  onChange: (next: ColumnMapping) => void;
}) {
  const choices = useMemo(() => headers.filter((h) => h.trim() !== ""), [headers]);
  const set = (field: MappingField, value: string | null) =>
    onChange({ ...mapping, columns: { ...mapping.columns, [field]: value } });

  return (
    <div style={CARD}>
      <span style={LABEL}>Columns</span>
      <div style={GRID}>
        {CARD_FIELDS.map((field) => {
          const required = REQUIRED_FIELDS.includes(field);
          const isMissing = missing.includes(field);
          const current = mapping.columns[field] ?? "";
          return (
            <label key={field} style={{ display: "contents" }}>
              <span style={{ color: isMissing ? "var(--qf-warning, #ffb74d)" : "var(--qf-text-secondary)" }}>
                {FIELD_LABEL[field]}
                {required ? " *" : ""}
              </span>
              <span style={ROW}>
                <select
                  style={{ ...INPUT, minWidth: 180, borderColor: isMissing ? "var(--qf-warning, #ffb74d)" : undefined }}
                  value={current}
                  disabled={busy}
                  onChange={(e) => set(field, e.target.value || null)}
                >
                  <option value="">
                    {isMissing ? "choose…" : field === "time" ? "— none (date only) —" : "— none —"}
                  </option>
                  {choices.map((h) => (
                    <option key={h} value={h}>
                      {h}
                    </option>
                  ))}
                </select>
                {field === "fillId" && !current && (
                  <span style={MUTED}>without one, duplicates match on a hash</span>
                )}
              </span>
            </label>
          );
        })}
        <label style={{ display: "contents" }}>
          <span style={{ color: "var(--qf-text-secondary)" }}>Time zone</span>
          <select
            style={{ ...INPUT, minWidth: 180 }}
            value={mapping.timezone}
            disabled={busy}
            onChange={(e) => onChange({ ...mapping, timezone: e.target.value })}
          >
            {(TIMEZONES.includes(mapping.timezone) ? TIMEZONES : [mapping.timezone, ...TIMEZONES]).map((tz) => (
              <option key={tz} value={tz}>
                {tz}
              </option>
            ))}
          </select>
        </label>
      </div>
    </div>
  );
}

function Result({
  summary,
  bookName,
  showProblems,
  onToggleProblems,
  onDone,
}: {
  summary: StagedImport["summary"] & object;
  bookName: string;
  showProblems: boolean;
  onToggleProblems: () => void;
  onDone: () => void;
}) {
  const skipped = Object.entries(summary.skipped);
  const needs = summary.problems.length + summary.orphans + summary.undefinedRisk;
  return (
    <>
      <div style={MUTED}>
        Into <b style={{ color: "var(--qf-text)" }}>{bookName}</b>
      </div>
      <div style={KV}>
        <span style={{ color: "var(--qf-text-secondary)" }}>Fills imported</span>
        <span style={{ fontVariantNumeric: "tabular-nums" }}>{summary.fillsImported.toLocaleString()}</span>
        <span style={{ color: "var(--qf-text-secondary)" }}>Skipped as duplicate</span>
        <span style={{ fontVariantNumeric: "tabular-nums" }}>{summary.duplicatesSkipped}</span>
        {summary.duplicatesKept > 0 && (
          <>
            <span style={{ color: "var(--qf-text-secondary)" }}>Kept although matched</span>
            <span style={{ fontVariantNumeric: "tabular-nums" }}>{summary.duplicatesKept}</span>
          </>
        )}
        <span style={{ color: "var(--qf-text-secondary)" }}>Round trips built</span>
        <span style={{ fontVariantNumeric: "tabular-nums" }}>{summary.roundTrips}</span>
        <span style={{ color: "var(--qf-text-secondary)" }}>Still open</span>
        <span style={{ fontVariantNumeric: "tabular-nums" }}>{summary.stillOpen}</span>
        <span style={{ color: "var(--qf-text-secondary)" }}>Expire / assign / exercise</span>
        <span style={{ fontVariantNumeric: "tabular-nums" }}>{summary.expiredAssignedExercised} closed</span>
      </div>
      {needs > 0 && (
        <div style={WARN}>
          {[
            summary.problems.length > 0 && `${summary.problems.length} rows could not be read`,
            summary.orphans > 0 &&
              `${summary.orphans} closing fill${summary.orphans === 1 ? "" : "s"} with no opening fill (history starts too late?)`,
            summary.undefinedRisk > 0 &&
              `${summary.undefinedRisk} undefined-risk option trade${summary.undefinedRisk === 1 ? "" : "s"} (margin estimated as risk)`,
          ]
            .filter(Boolean)
            .join(" · ")}
          {summary.problems.length > 0 && (
            <>
              {" "}
              <button type="button" style={LINK} onClick={onToggleProblems}>
                {showProblems ? "hide" : "show rows"}
              </button>
            </>
          )}
        </div>
      )}
      {showProblems && (
        <ul style={{ ...MUTED, margin: 0, paddingLeft: 18 }}>
          {summary.problems.map((p) => (
            <li key={p.row}>
              row {p.row}: {p.reason}
            </li>
          ))}
        </ul>
      )}
      {skipped.length > 0 && (
        <div style={MUTED}>
          Not trades, left out: {skipped.map(([action, n]) => `${n} × ${action}`).join(", ")}
        </div>
      )}
      <div style={MUTED}>P&amp;L is {summary.pnlBasis}.</div>
      <div style={FOOT}>
        <span style={{ flex: 1 }} />
        <button type="button" style={PRIMARY} onClick={onDone}>
          Done
        </button>
      </div>
    </>
  );
}

function DropZone({ busy, onFile }: { busy: boolean; onFile: (f: File) => void }) {
  const [over, setOver] = useState(false);
  return (
    <label
      style={{ ...DROP, borderColor: over ? "var(--qf-accent)" : "var(--qf-border)" }}
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        const f = e.dataTransfer.files?.[0];
        if (f) onFile(f);
      }}
    >
      {busy ? "Reading…" : "Drop a broker CSV here, or click to choose a file"}
      <input
        type="file"
        accept=".csv,text/csv"
        style={{ display: "none" }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
          e.target.value = "";
        }}
      />
    </label>
  );
}

function baseName(name?: string): string {
  return (name ?? "Imported").replace(/\.[^.]+$/, "").slice(0, 80) || "Imported";
}

function formatBytes(n: number): string {
  return n < 1024 * 1024 ? `${Math.max(1, Math.round(n / 1024))} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function stepStyle(s: Step, current: Step): React.CSSProperties {
  const order: Step[] = ["file", "duplicates", "result"];
  const done = order.indexOf(s) < order.indexOf(current);
  const on = s === current;
  return {
    padding: "1px 8px",
    borderRadius: 99,
    border: `1px solid ${on ? "var(--qf-accent)" : "var(--qf-border)"}`,
    color: on ? "var(--qf-accent)" : done ? "var(--qf-positive)" : "var(--qf-text-muted)",
    fontSize: 11,
  };
}

const PANEL: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 10,
  width: 620,
  maxWidth: "94vw",
  maxHeight: "76vh",
  overflowY: "auto",
  padding: 14,
  color: "var(--qf-text)",
  font: "13px system-ui, sans-serif",
};
const STEPS: React.CSSProperties = { display: "flex", gap: 6, flexWrap: "wrap" };
const CARD: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 6,
  padding: "8px 10px",
  border: "1px solid var(--qf-border)",
  borderRadius: 6,
  background: "var(--qf-surface)",
};
const LABEL: React.CSSProperties = {
  fontSize: 10.5,
  letterSpacing: 0.6,
  textTransform: "uppercase",
  color: "var(--qf-text-muted)",
};
const ROW: React.CSSProperties = { display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" };
const GRID: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "auto 1fr",
  gap: "5px 12px",
  alignItems: "center",
};
const INPUT: React.CSSProperties = {
  font: "inherit",
  fontSize: 12,
  color: "var(--qf-text)",
  background: "var(--qf-surface-alt)",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  padding: "2px 6px",
};
const BTN: React.CSSProperties = {
  ...INPUT,
  padding: "4px 10px",
  cursor: "pointer",
};
const PRIMARY: React.CSSProperties = {
  ...BTN,
  background: "var(--qf-accent)",
  borderColor: "var(--qf-accent)",
  color: "var(--qf-background)",
  fontWeight: 600,
};
const LINK: React.CSSProperties = {
  background: "none",
  border: 0,
  padding: 0,
  color: "var(--qf-accent)",
  cursor: "pointer",
  font: "inherit",
};
const FOOT: React.CSSProperties = { display: "flex", gap: 6, alignItems: "center", marginTop: 4 };
const MUTED: React.CSSProperties = { color: "var(--qf-text-secondary)", fontSize: 12, lineHeight: 1.5 };
const WARN: React.CSSProperties = {
  border: "1px solid var(--qf-warning, #ffb74d)",
  color: "var(--qf-warning, #ffb74d)",
  borderRadius: 5,
  padding: "7px 10px",
  fontSize: 12,
};
const ERROR: React.CSSProperties = { ...WARN, borderColor: "var(--qf-negative)", color: "var(--qf-negative)" };
const DROP: React.CSSProperties = {
  display: "block",
  border: "1.5px dashed var(--qf-border)",
  borderRadius: 6,
  padding: 20,
  textAlign: "center",
  color: "var(--qf-text-secondary)",
  cursor: "pointer",
};
const SCROLL: React.CSSProperties = { overflow: "auto", maxWidth: "100%" };
const TABLE: React.CSSProperties = { borderCollapse: "collapse", width: "100%", fontSize: 12 };
const TH: React.CSSProperties = {
  textAlign: "left",
  fontWeight: 500,
  color: "var(--qf-text-muted)",
  padding: "4px 8px",
  borderBottom: "1px solid var(--qf-border)",
  whiteSpace: "nowrap",
};
const TD: React.CSSProperties = {
  padding: "4px 8px",
  borderBottom: "1px solid var(--qf-border)",
  whiteSpace: "nowrap",
};
const NUM: React.CSSProperties = { ...TD, textAlign: "right", fontVariantNumeric: "tabular-nums" };
const KV: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "auto auto",
  justifyContent: "start",
  gap: "3px 24px",
  margin: 0,
  fontSize: 12,
};
